// serializer
import WalletTransactionGroupSerializer from "issara-sdk/types/WalletTransactionGroupSerializer_apps_BIL";
import WalletTransactionListSerializer from "issara-sdk/types/WalletTransactionListSerializer_apps_BIL";
import WalletTypeSerializer from "issara-sdk/types/WalletTypeSerializer_apps_BIL";

import formatComma from "react-lib/utils/formatComma";

import {
  PAY_KINDS,
  payLineErrorOf,
  round2,
  type PayLine,
  type PayMaster,
  type PaySummary,
} from "./billingPayModelV4";

// ---------- ภาพรวม ----------

export type WalletType = WalletTypeSerializer;

/** การฝาก 1 ครั้ง (BIL/wallet-tx-group-list/) = การ์ด 1 ใบ */
export type WalletGroup = WalletTransactionGroupSerializer;

/** แถวของ BIL/wallet-transaction-patient/ */
export type WalletTx = WalletTransactionListSerializer;

export type WalletStatus = "" | "closed" | "partial" | "empty";

export type WalletCard = {
  group: WalletGroup;
  name: string;
  code: string;
  paid: number;
  used: number;
  refunded: number;
  balance: number;
  status: WalletStatus;
};

export const WALLET_STATUS_LABELS: Record<Exclude<WalletStatus, "">, string> = {
  closed: "ยกเลิกแล้ว · คืนเงิน",
  partial: "คืนเงินบางส่วน",
  empty: "ใช้หมดแล้ว",
};

// type ของ wallet-transaction: 5 WITHDRAW, 6 PAYMENT, 7 REFUND
const TX_PAYMENT = 6;
const TX_REFUNDS = [5, 7];

/** การ์ดต่อการฝาก: ชื่อรายการ ยอดได้รับ ใช้ไป คืนแล้ว คงเหลือ และป้ายสถานะ */
export const walletCardsOf = (
  groups: WalletGroup[],
  txs: WalletTx[],
  types: WalletType[],
): WalletCard[] =>
  groups.map((group) => {
    const type = types.find((item) => item.id === group.wallet_type);
    const balance = Number(group.balance) || 0;
    let used = 0;
    let refunded = 0;

    for (const tx of txs) {
      if (tx.group !== group.id) {
        continue;
      }

      if (tx.type === TX_PAYMENT) {
        used += Math.abs(Number(tx.value) || 0);
      }

      if (TX_REFUNDS.includes(tx.type)) {
        refunded += Math.abs(Number(tx.value) || 0);
      }
    }

    let status: WalletStatus = "";

    if (group.status === "REFUND" || group.status === "CANCELED") {
      status = "closed";
    } else if (refunded > 0) {
      status = "partial";
    } else if (balance <= 0) {
      status = "empty";
    }

    return {
      group,
      name: type?.name || "-",
      code: type?.code || "",
      paid: Number(group.init_balance) || 0,
      used: round2(used),
      refunded: round2(refunded),
      balance,
      status,
    };
  });

// ---------- ประวัติรายการ ----------

export type TxKind = "deposit" | "payment" | "refund";

export type TxFilter = "all" | TxKind;

export type HistoryRow = {
  tx: WalletTx;
  kind: TxKind | null;
  name: string;
  ref: string;
  staff: string;
};

export const TX_KIND_LABELS: Record<TxKind, string> = {
  deposit: "ฝาก",
  payment: "ตัดชำระ",
  refund: "คืนเงิน",
};

export const TX_FILTER_OPTIONS: { value: TxFilter; label: string }[] = [
  { value: "all", label: "ทุกรายการ" },
  { value: "deposit", label: "ฝาก" },
  { value: "payment", label: "ตัดชำระ" },
  { value: "refund", label: "คืนเงิน" },
];

// type ของ wallet-transaction -> รายการ
const TX_KINDS: Record<number, TxKind> = {
  1: "deposit",
  5: "refund",
  6: "payment",
  7: "refund",
};

/** แถวประวัติ ใหม่สุดก่อน กรองตาม Wallet (group) และรายการ */
export const historyRowsOf = (
  txs: WalletTx[],
  cards: WalletCard[],
  groupId: number | null,
  filter: TxFilter,
): HistoryRow[] =>
  txs
    .filter(
      (tx) =>
        (groupId === null || tx.group === groupId) &&
        (filter === "all" || TX_KINDS[tx.type] === filter),
    )
    .sort((a, b) => (b.created || "").localeCompare(a.created || ""))
    .map((tx) => {
      const card = cards.find((item) => item.group.id === tx.group);
      const kind = TX_KINDS[tx.type] ?? null;

      return {
        tx,
        kind,
        name: card?.name || "-",
        ref: tx.payment_receipt_no || tx.deposit_receipt_no || "—",
        staff:
          tx.payment_cashier || (kind === "deposit" ? card?.group.receipt_create_user : "") || "—",
      };
    });

// ---------- ฝากเงินมัดจำ ----------

/** ช่องทางของการฝาก (ไม่มีคูปองเงินสด) */
export const DEPOSIT_KINDS = PAY_KINDS.filter((item) => item.kind !== "cpn");

export type DepositReceiptLine = { label: string; detail: string; value: number };

/** ข้อความเตือนใต้ฟอร์มฝาก (ข้อแรกที่ไม่ผ่าน) */
export const depositHintOf = (data: {
  amount: number;
  typeId: string;
  cash: string;
  summary: PaySummary;
  lines: PayLine[];
  tried: boolean;
}) => {
  if (!(data.amount > 0)) {
    return "กรอกจำนวนเงินมัดจำมากกว่า 0";
  }

  if (data.cash.trim() && Number.isNaN(Number(data.cash))) {
    return "ยอดเงินสดต้องเป็นตัวเลข";
  }

  const errors = data.lines.map(payLineErrorOf);

  if (data.tried && errors.some((error) => Object.keys(error).length > 0)) {
    return `กรอกรายละเอียดการชำระให้ครบ (ช่องที่มีกรอบแดง)${
      errors.some((error) => error.card) ? " · เลขบัตรต้องมีอย่างน้อย 4 หลักท้าย" : ""
    }`;
  }

  if (data.summary.others > data.amount) {
    return "ยอดที่ไม่ใช่เงินสดรวมกันเกินจำนวนเงินมัดจำ · เงินทอนได้เฉพาะเงินสด";
  }

  if (data.summary.total < data.amount) {
    return `ยอดชำระยังไม่ครบ ขาดอีก ${formatComma(data.amount - data.summary.total)} บาท`;
  }

  if (!data.typeId) {
    return "เลือกรายการเงินมัดจำ";
  }

  return "";
};

/** แถวแยกตามรูปแบบการชำระบนใบรับมัดจำ */
export const depositReceiptLinesOf = (
  cashPaid: number,
  lines: PayLine[],
  accounts: PayMaster[],
  edcs: PayMaster[],
): DepositReceiptLine[] => {
  const rows: DepositReceiptLine[] = [];

  if (cashPaid > 0) {
    rows.push({ label: "เงินสด", detail: "", value: cashPaid });
  }

  for (const line of lines) {
    const label = PAY_KINDS.find((item) => item.kind === line.kind)?.label || "";
    let detail = "";

    if (line.kind === "cc" || line.kind === "dc") {
      const edc = edcs.find((item) => String(item.id) === line.edc);

      detail = `**** ${line.card[3]}${edc ? ` · ${edc.name}` : ""}`;
    }

    if (line.kind === "qr") {
      const account = accounts.find((item) => String(item.id) === line.account);

      detail = `${account?.name || ""}${line.ref.trim() ? ` · อ้างอิง ${line.ref.trim()}` : ""}`;
    }

    if (line.kind === "chq") {
      detail = `เช็ค ${line.cheque.trim()} ลว. ${line.date}`;
    }

    rows.push({ label, detail, value: Number(line.amount) || 0 });
  }

  return rows;
};

export const depositFlashOf = (
  name: string,
  amount: number,
  rows: DepositReceiptLine[],
  code?: string,
) =>
  `รับฝาก ${name} ${formatComma(amount)} บาท (${rows.map((row) => row.label).join(" + ")})${
    code ? ` · ออกใบรับมัดจำ ${code}` : ""
  } · พร้อมใช้ที่หน้ารับชำระ`;

// ---------- ยกเลิกและคืนเงินมัดจำ ----------

export type RefundChannel = "cash" | "transfer" | "card";

export type RefundForm = {
  channel: RefundChannel;
  account: string;
  reason: string;
  username: string;
  password: string;
};

export const REFUND_CHANNELS: { value: RefundChannel; label: string }[] = [
  { value: "cash", label: "คืนเป็นเงินสด" },
  { value: "transfer", label: "โอนเข้าบัญชีผู้รับบริการ" },
  { value: "card", label: "คืนเข้าบัตรเดิม (Void / Refund ที่เครื่องรูด)" },
];

export const refundErrorsOf = (form: RefundForm) => {
  const errors: string[] = [];

  if (form.channel === "transfer" && !form.account.trim()) {
    errors.push("กรอกเลขที่บัญชี / ชื่อบัญชี");
  }

  if (!form.reason.trim()) {
    errors.push("ระบุเหตุผล");
  }

  if (!form.username.trim()) {
    errors.push("กรอกชื่อผู้ใช้");
  }

  if (!form.password) {
    errors.push("กรอกรหัสผ่านยืนยัน");
  }

  return errors;
};

/** note ของ wallet-tx-group-cancel: เหตุผล · ช่องทางคืนเงิน[ บัญชี] */
export const refundNoteOf = (form: RefundForm) => {
  const channel = REFUND_CHANNELS.find((item) => item.value === form.channel)?.label || "";

  return `${form.reason.trim()} · ${channel}${
    form.channel === "transfer" ? ` ${form.account.trim()}` : ""
  }`;
};

export const refundFlashOf = (card: WalletCard, form: RefundForm) =>
  `ยกเลิกเงินมัดจำ · คืนเงิน ${card.name} ${formatComma(card.balance)} บาท (${
    REFUND_CHANNELS.find((item) => item.value === form.channel)?.label || ""
  })`;
